import { useState } from 'react';
import { TRACKED_REG } from '../lib/flightTrack';
import type { LucideIcon } from 'lucide-react';
import {
  Plane,
  CalendarPlus,
  ClipboardCheck,
  PlaneLanding,
  TriangleAlert,
  CreditCard,
  Compass,
  X,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

// First-login guided tour for a newly-active lessee. A stepped modal (carousel)
// that explains the portal's main areas + what's expected — no DOM coachmarks, so
// it can't break when pages change. Shown once per device, replayable from the
// dashboard. Dismissal is persisted in localStorage keyed by uid.
const SEEN_PREFIX = 'ajm-welcome-tour-v1:';

export function tourSeen(uid: string | null | undefined): boolean {
  if (!uid || typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(SEEN_PREFIX + uid) === '1';
  } catch {
    return false;
  }
}

function markSeen(uid: string | null | undefined) {
  if (!uid || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(SEEN_PREFIX + uid, '1');
  } catch {
    /* ignore */
  }
}

interface Step {
  icon: LucideIcon;
  title: string;
  nav: string;
  body: string;
}

const STEPS: Step[] = [
  {
    icon: Plane,
    title: 'Welcome aboard',
    nav: 'Dashboard',
    body: `This is your home for flying ${TRACKED_REG} — book the airplane, log your flights, report squawks, and handle billing, all in one place. Here's the 30-second tour.`,
  },
  {
    icon: CalendarPlus,
    title: 'Book a flight',
    nav: 'Book a Flight',
    body: `Reserve the airplane on the calendar — pick your date and times. Bookings confirm instantly. You can see everyone’s reservations so it’s easy to coordinate, and the live Tracker shows where ${TRACKED_REG} is right now.`,
  },
  {
    icon: ClipboardCheck,
    title: 'Before you fly',
    nav: 'Pre-flight',
    body: 'Run the pre-flight checklist: confirm your currency, check the weather and any open squawks, and check the oil (the approved oil spec is listed right there). You are pilot in command with full operational control of your flight.',
  },
  {
    icon: PlaneLanding,
    title: 'After you land',
    nav: 'My Flights → Log a flight',
    body: 'Log your Hobbs and tach before you walk away. Billing is automatic from the card on file — there is no monthly minimum, you pay only for the Hobbs hours you fly at $100/hr. Secure the airplane and bring it back to spot C3 at KLGB.',
  },
  {
    icon: TriangleAlert,
    title: 'See something? Squawk it',
    nav: 'Squawks',
    body: 'Report any issue, damage, or concern right away — within 24 hours. Squawks are a shared log every pilot sees, so the next person stays safe. Never fly a known grounding squawk.',
  },
  {
    icon: CreditCard,
    title: 'Stay current & covered',
    nav: 'Documents · Billing',
    body: 'Keep your medical, flight review, badge, and renter’s insurance current in Documents, and your card valid in Billing. New: the Aircraft & Avionics page has the full Garmin glass panel with links to learn each unit before your checkout.',
  },
];

export function WelcomeTour({ uid, onClose }: { uid: string | null | undefined; onClose: () => void }) {
  const [i, setI] = useState(0);
  const step = STEPS[i];
  const last = i === STEPS.length - 1;

  function close() {
    markSeen(uid);
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy-900/40 p-4">
      <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-xl">
        {/* header band */}
        <div className="relative bg-brand px-6 pb-5 pt-6 text-white">
          <button
            type="button"
            onClick={close}
            aria-label="Skip the tour"
            className="absolute right-3 top-3 rounded-lg p-1.5 text-white/70 transition-colors hover:bg-white/10 hover:text-white"
          >
            <X size={18} />
          </button>
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/10">
            <step.icon size={22} />
          </span>
          <div className="mt-3 text-[11px] font-medium uppercase tracking-wider text-sky-100">
            <Compass size={12} className="mr-1 inline" />
            {step.nav}
          </div>
          <h2 className="mt-0.5 font-display text-xl font-semibold text-white">{step.title}</h2>
        </div>

        {/* body */}
        <div className="px-6 py-5">
          <p className="text-sm leading-relaxed text-ink">{step.body}</p>

          {/* dots */}
          <div className="mt-5 flex items-center justify-center gap-1.5">
            {STEPS.map((_, idx) => (
              <button
                key={idx}
                type="button"
                aria-label={`Go to step ${idx + 1}`}
                onClick={() => setI(idx)}
                className={`h-1.5 rounded-full transition-all ${
                  idx === i ? 'w-5 bg-brand' : 'w-1.5 bg-slate-300 hover:bg-slate-400'
                }`}
              />
            ))}
          </div>

          {/* controls */}
          <div className="mt-5 flex items-center justify-between gap-3">
            {i > 0 ? (
              <button
                type="button"
                onClick={() => setI((v) => v - 1)}
                className="inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-ink-muted hover:bg-slate-100 hover:text-navy-900"
              >
                <ChevronLeft size={16} /> Back
              </button>
            ) : (
              <button
                type="button"
                onClick={close}
                className="rounded-lg px-3 py-2 text-sm font-medium text-ink-subtle hover:text-ink-muted"
              >
                Skip
              </button>
            )}
            {last ? (
              <button type="button" onClick={close} className="btn-primary py-2 text-sm">
                Got it — let&apos;s fly
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setI((v) => v + 1)}
                className="btn-primary inline-flex items-center gap-1 py-2 text-sm"
              >
                Next <ChevronRight size={16} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
